/**
 * #2058 — SoulInterviewTurnCard
 *
 * A CHAT-STREAM-NATIVE card that renders one adaptive soul-intake turn. The lead
 * wires this into the message stream (Workspace.tsx) as part of the integrated-interview
 * surface (`CRUCIBLE_INTAKE=1` — crucible#2682 collapsed the old standalone
 * `CRUCIBLE_INTERVIEW_INTEGRATED` var into this one primary gate); it is NOT
 * mounted in the modal.
 *
 * Props mirror `SoulIntakeTurnView` exactly so the IPC turn can be passed directly.
 * The card is SELF-CONTAINED: it calls back via `onAnswer` (no direct IPC — the
 * parent handles the IPC invoke so this component stays testable with zero bridge mocks).
 *
 * Multimodal (#2058 v2): images are decoupled from the turn wire. The parent
 * (`IntegratedSoulInterview`) subscribes to `intake:visual` and renders a
 * `SoulInterviewVisualCard` alongside the active turn card. This card is text-only.
 *
 * Thinking state: while the next turn is being generated the parent should pass
 * `thinking={true}`. This swaps the submit button to a "thinking" spinner and
 * optionally shows an elapsed tick (the "Shaping the next question…" row) so the
 * ~6-10s LLM pause never reads as frozen.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Sparkles } from 'lucide-react';
import type {
  SoulIntakeTurnView,
  SoulIntakeTurnVisual,
  SoulIntakeQuestionView,
  TurnVisualMedia,
} from '@volley/crucible-ipc-types';
import { ArrowIcon, MicIcon, SteerComposer } from './SteerComposer.js';
import { useSpeechFeedback } from '../hooks/useSpeechFeedback.js';

// ── Types ──────────────────────────────────────────────────────────────────────

export interface SoulInterviewAnswerInput {
  optionIndex?: number;
  freeText?: string;
}

type SoulInterviewCommandSource = 'voice' | 'text';

const DECIDE_FOR_ME_COMMANDS = new Set([
  'decide for me',
  'you choose',
  'you decide',
  'choose for me',
  'pick for me',
]);

export function isDecideForMeCommand(command: string): boolean {
  const normalized = command
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
  return DECIDE_FOR_ME_COMMANDS.has(normalized);
}

function recommendedOptionIndex(question: SoulIntakeQuestionView | null): number | null {
  if (!question) return null;
  const index = question.recommended;
  return typeof index === 'number' && Number.isInteger(index) && question.options[index]
    ? index
    : null;
}

export interface SoulInterviewTurnCardProps {
  /** The current adaptive turn from the main-process proxy. */
  turn: SoulIntakeTurnView;
  /** True while the next turn's LLM call is in flight — swaps submit to a thinking row. */
  thinking?: boolean;
  /** Called with the creator's answer when they hit Next. */
  onAnswer: (answer: SoulInterviewAnswerInput) => void;
  /** Phone/right-panel command primitive: raw voice/text goes to the main boundary. */
  onCommand?: (command: string, source: SoulInterviewCommandSource) => void;
  /**
   * 2026-07-13 — the moodboard turn's asset-steering primitive: phone-side
   * feedback ("make it warmer, add lanterns") that regenerates the single
   * board WITHOUT settling the turn (unlike `onAnswer`/`onCommand`, both of
   * which are terminal for this turn). Omitted outside the moodboard turn.
   */
  onSteer?: (feedback: string) => void;
  /** True while a `onSteer` regeneration is in flight — disables the steer
   *  input/lock-in action and shows an "Updating…" state on both surfaces. */
  steering?: boolean;
  /** Emits the local draft selection so the paired TV surface can mirror it. */
  onDraftChange?: (answer: SoulInterviewAnswerInput) => void;
  /** Optional: elapsed seconds to display in the thinking row. */
  thinkingElapsedSec?: number;
  /** The answer is waiting for the durable checkpoint commit. */
  saving?: boolean;
  /** Phone mode removes story context; the TV carries it. `'tv'` is the
   *  10-foot variant of the same chat-native card (crucible#2610 Phase 3
   *  consumer-preview mount): larger type, no free-text input (the phone
   *  card is where the player types/speaks). */
  surface?: 'chat' | 'phone' | 'tv' | 'desktop';
  /** Guided-only terminal action rendered beside Continue on desktop. */
  onFinishInterview?: () => void;
  /** Determines the desktop terminal action copy for restored build/design interviews. */
  finishInterviewTarget?: 'build' | 'design';
  finishInterviewDisabled?: boolean;
  /**
   * #2058 P2 — for the moodboard turn (`turn.kind === 'moodboard'`), the candidate
   * board images keyed by board index (0..2). A present key with a value = the
   * board's image; a present key with `undefined` = shimmer (generating); an
   * absent key = shimmer too (not yet pushed). `null` = generation gave up
   * (IntegratedSoulInterview's timeout backstop) — a plain "no preview"
   * placeholder, not an indefinite shimmer. Omitted/empty → the moodboard
   * turn renders text-only captions.
   */
  boardVisuals?: ReadonlyMap<number, TurnVisualMedia | null | undefined>;
  boardVisualAttempts?: ReadonlyMap<number, SoulIntakeTurnVisual['attempt']>;
  onRetryVisual?: () => void;
}

// ── Component ─────────────────────────────────────────────────────────────────

/**
 * One adaptive turn rendered as a chat-native card. Handles the REVEAL (turn 0
 * with `direction` + optional moodboard image) and follow-up questions (`question`
 * only). The card is stateless about the conversation; state for the in-progress
 * answer lives here only until Next is tapped.
 */
export function SoulInterviewTurnCard({
  turn,
  thinking = false,
  onAnswer,
  onCommand,
  onDraftChange,
  onSteer,
  steering = false,
  thinkingElapsedSec,
  saving = false,
  surface = 'chat',
  onFinishInterview,
  finishInterviewTarget = 'build',
  finishInterviewDisabled = false,
  boardVisuals,
  boardVisualAttempts,
  onRetryVisual,
}: SoulInterviewTurnCardProps): React.JSX.Element {
  // 2026-07-09 — nothing is pre-picked (Sean, live-testing: "nothing should
  // be auto-picked. the user can free form answer or even blend ideas").
  // `null` = no explicit choice yet; the AI's "Bold" recommendation still
  // shows as a badge on its option (question.recommended), it just no
  // longer silently pre-selects/pre-checks that option before the user has
  // done anything.
  const [picked, setPicked] = useState<number | null>(null);
  const [pickedViaRecommendation, setPickedViaRecommendation] = useState(false);
  const [freeText, setFreeText] = useState('');
  const speechBaseRef = useRef('');
  const [commandSource, setCommandSource] = useState<SoulInterviewCommandSource>('text');
  const phoneSurface = surface === 'phone';
  const tvSurface = surface === 'tv';
  const desktopSurface = surface === 'desktop';
  const handleSpeechTranscript = useCallback((transcript: string) => {
    const base = speechBaseRef.current.trimEnd();
    setFreeText(`${base}${base ? ' ' : ''}${transcript}`);
    setCommandSource('voice');
  }, []);
  const speech = useSpeechFeedback({
    active: Boolean(turn.question) && !thinking && !saving,
    onTranscript: handleSpeechTranscript,
  });

  // Reset local pick state when the turn changes (new question).
  // Keying the card on turn.questionNumber in the parent is the preferred pattern,
  // but this guards against the key-less case too.
  useEffect(() => {
    setPicked(null);
    setPickedViaRecommendation(false);
    setFreeText('');
  }, [turn.questionNumber]);

  useEffect(() => {
    if (!turn.question) return;
    onDraftChange?.({
      ...(picked !== null ? { optionIndex: picked } : {}),
      ...(freeText.trim() ? { freeText: freeText.trim() } : {}),
    });
  }, [freeText, onDraftChange, picked, turn.question]);

  const handlePick = useCallback((oi: number) => {
    setPicked(oi);
    setPickedViaRecommendation(false);
  }, []);

  const handleDecideForMe = useCallback(() => {
    const recommendation = recommendedOptionIndex(turn.question);
    if (recommendation === null) return;
    setPicked(recommendation);
    setPickedViaRecommendation(true);
    setFreeText('');
    setCommandSource('text');
  }, [turn.question]);

  const handleVoice = useCallback(() => {
    if (speech.listening) {
      speech.stop();
      return;
    }
    speechBaseRef.current = freeText;
    speech.start();
  }, [freeText, speech]);

  const handleSubmit = useCallback(() => {
    if (thinking || saving || !turn.question) return;
    if (picked === null && freeText.trim().length === 0) return;
    const recommendation = recommendedOptionIndex(turn.question);
    if (picked === null && recommendation !== null && isDecideForMeCommand(freeText)) {
      onAnswer({ optionIndex: recommendation });
      return;
    }
    onAnswer({
      ...(picked !== null ? { optionIndex: picked } : {}),
      ...(freeText.trim() ? { freeText: freeText.trim() } : {}),
    });
  }, [thinking, saving, turn.question, picked, freeText, onAnswer]);

  const handleCommandSubmit = useCallback(() => {
    const command = freeText.trim();
    if (thinking || !turn.question || !command) return;
    onCommand?.(command, commandSource);
  }, [commandSource, freeText, onCommand, thinking, turn.question]);

  const pickedLabel = picked !== null ? turn.question?.options[picked]?.label : undefined;
  const isMoodboardTurn = turn.kind === 'moodboard';

  const handleSteerSubmit = useCallback(() => {
    const text = freeText.trim();
    if (thinking || steering || !text || !onSteer) return;
    onSteer(text);
    setFreeText('');
  }, [thinking, steering, freeText, onSteer]);

  const handleLockIn = useCallback(() => {
    if (thinking || steering) return;
    onAnswer({ optionIndex: 0 });
  }, [thinking, steering, onAnswer]);

  // 2026-07-13 — the moodboard turn's phone surface is a dedicated asset-
  // steering composer, not the generic answer input below: free-text/voice
  // feeds `onSteer` (regenerate the single board, non-terminal) instead of
  // `onCommand`/`onAnswer`, plus an explicit versioned confirmation action that
  // terminates the turn. Mirrors the build-time asset-review phone driver's
  // visual language (`TvSteeringSurface.tsx`'s `PhoneSteeringDriver`) per
  // Sean's steer: "it's all potential consumer surface so it should look
  // unified."
  if (phoneSurface && isMoodboardTurn) {
    // 2026-07-14 — Sean, live-testing: the phone doesn't need the board image
    // at all — the TV already shows it (`MoodboardSingleBoard`), and a stuck
    // "Generating…" on the phone (this branch never distinguished the
    // backstop-timeout `null` from still-in-flight `undefined` the way the TV
    // does) read as broken when the TV had already honestly given up. Phone
    // is text/voice status only now; the image and its generating/gave-up/
    // steering states live on the TV exclusively.
    const visual = boardVisuals?.get(0);
    const ready = visual != null;
    // Same honesty rule as the TV's MoodboardSingleBoard: `null` means the
    // backstop timeout gave up, `undefined`/absent means still in flight —
    // collapsing them into one "Generating…" text would silently repeat the
    // bug just fixed, only in prose instead of an animated shimmer.
    const gaveUp = visual === null;
    const statusLabel = steering
      ? 'Updating the look'
      : ready
        ? 'Look ready'
        : gaveUp
          ? 'No preview this time'
          : 'Generating the look';
    const statusDetail = steering
      ? 'Check the TV for the update.'
      : ready
        ? 'Check the TV, then say what to change or lock it in.'
        : gaveUp
          ? 'You can still describe changes or lock it in.'
          : 'Check the TV — this can take a moment.';
    return (
      <div
        className="relative flex min-h-0 flex-1 flex-col overflow-hidden rounded-[1.75rem] border border-white/12 bg-[#050409] shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]"
        data-testid="soul-interview-turn-card"
      >
        <div
          className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden p-4"
          data-testid="soul-moodboard-phone-steering"
          data-input-primitive="voice-first-text-fallback"
        >
          <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-2 text-center">
            <p
              data-testid="soul-moodboard-phone-status"
              className="text-[12px] font-semibold uppercase tracking-[0.2em] text-crucible-accent/85"
            >
              {statusLabel}
            </p>
            <p className="text-[13px] text-white/55">{statusDetail}</p>
          </div>

          {speech.error && (
            <p
              role="alert"
              data-testid="soul-interview-voice-error"
              className="shrink-0 rounded-xl bg-red-500/15 px-3 py-2 text-center text-[12px] text-red-200"
            >
              {speech.error}
            </p>
          )}

          <div className="mt-auto flex shrink-0 flex-col gap-2">
            {/* crucible#3187 — the mic+textarea+send row now lives in the
                shared SteerComposer (extracted from this exact block); the
                same testids are passed through so nothing downstream (this
                file's own tests + the phone e2e specs) needed to change. */}
            <SteerComposer
              value={freeText}
              onChange={(value) => {
                setFreeText(value);
                setCommandSource('text');
              }}
              onSubmit={handleSteerSubmit}
              disabled={thinking || steering}
              placeholder="e.g. make it warmer, add lanterns"
              textareaAriaLabel="Tell Foundry what to change"
              micTitle={speech.supported ? 'Speak a change' : 'Type a change'}
              micAriaLabel="Voice steering"
              sendAriaLabel="Send change"
              listening={speech.listening}
              onMicToggle={handleVoice}
              testIds={{
                shell: 'soul-interview-phone-input-shell',
                micButton: 'soul-interview-voice-button',
                input: 'soul-moodboard-steer-input',
                send: 'soul-moodboard-steer-send',
              }}
            />
            <button
              type="button"
              data-testid="soul-moodboard-lock-in"
              onClick={handleLockIn}
              disabled={thinking || steering}
              className="glint-halo brand-gradient-fill inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-xl bg-crucible-accent px-5 py-2.5 text-sm font-semibold text-black shadow-lg shadow-crucible-accent/20 transition hover:brightness-110 disabled:cursor-default disabled:opacity-50"
            >
              {turn.question?.options[0]?.label ?? 'Use this direction'}
              <ArrowIcon />
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (phoneSurface) {
    return (
      <div
        className="relative flex min-h-0 flex-1 flex-col overflow-hidden rounded-[1.75rem] border border-white/12 bg-[#050409] shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]"
        data-testid="soul-interview-turn-card"
      >
        <div
          className="flex min-h-0 flex-1 flex-col justify-end gap-4 overflow-hidden p-4"
          data-testid="soul-interview-phone-controller"
          data-input-primitive="voice-first-text-fallback"
        >
          <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-4 overflow-hidden">
            <button
              type="button"
              title={speech.supported ? 'Speak answer' : 'Type answer'}
              aria-label="Voice answer"
              aria-pressed={speech.listening}
              data-testid="soul-interview-voice-button"
              onClick={handleVoice}
              disabled={thinking || !turn.question}
              className={[
                'relative grid h-[min(46vw,158px)] min-h-[124px] w-[min(46vw,158px)] min-w-[124px] shrink-0 place-items-center rounded-full border text-white transition',
                'before:pointer-events-none before:absolute before:inset-3 before:rounded-full before:border before:border-white/6 before:bg-white/[0.025]',
                speech.listening
                  ? 'border-crucible-accent bg-crucible-accent text-black shadow-lg shadow-crucible-accent/30 before:border-black/10 before:bg-black/10'
                  : 'border-white/14 bg-[radial-gradient(circle_at_50%_38%,rgba(255,255,255,0.13),rgba(255,255,255,0.045)_58%,rgba(255,255,255,0.02))] hover:border-white/26 hover:bg-white/[0.08]',
                thinking || !turn.question ? 'cursor-not-allowed opacity-45' : '',
              ].join(' ')}
            >
              {speech.listening && (
                <span
                  className="absolute inset-[-7px] animate-pulse rounded-full border border-crucible-accent/55"
                  aria-hidden
                />
              )}
              <span className="relative z-10">
                <MicIcon size={50} />
              </span>
            </button>
            <p className="truncate text-center text-[12px] font-semibold uppercase tracking-[0.18em] text-white/42">
              {speech.listening ? 'Listening' : thinking ? 'Thinking' : 'Say your answer'}
            </p>
            {/* #2992 sweep — mic denial/failure must be loud, not a dead button. */}
            {speech.error && (
              <p
                role="alert"
                data-testid="soul-interview-voice-error"
                className="rounded-xl bg-red-500/15 px-3 py-2 text-center text-[12px] text-red-200"
              >
                {speech.error}
              </p>
            )}
          </div>

          <div
            className="flex shrink-0 items-end gap-2 rounded-2xl border border-white/12 bg-black/55 p-2 focus-within:border-crucible-accent/75"
            data-testid="soul-interview-phone-input-shell"
          >
            <textarea
              value={freeText}
              disabled={thinking || !turn.question}
              onChange={(e) => {
                setFreeText(e.target.value);
                setCommandSource('text');
              }}
              onKeyDown={(event) => {
                if (
                  event.key !== 'Enter' ||
                  event.shiftKey ||
                  event.altKey ||
                  event.ctrlKey ||
                  event.metaKey
                ) {
                  return;
                }
                event.preventDefault();
                handleCommandSubmit();
              }}
              rows={2}
              placeholder="option B, but make it cozier"
              aria-label="Tell Foundry your answer"
              aria-keyshortcuts="Enter"
              data-testid="soul-interview-command-input"
              className="min-h-[58px] flex-1 resize-none bg-transparent px-1.5 py-1.5 text-[16px] leading-snug text-white/92 placeholder:text-white/32 focus:outline-none disabled:opacity-45"
            />
            <button
              type="button"
              data-testid="soul-interview-command-send"
              aria-label="Send answer"
              disabled={thinking || !turn.question || freeText.trim().length === 0}
              onClick={handleCommandSubmit}
              className="grid h-11 w-11 shrink-0 place-items-center rounded-full border border-crucible-accent/35 text-crucible-accent transition hover:border-crucible-accent hover:bg-crucible-accent hover:text-black disabled:border-white/10 disabled:text-white/24 disabled:hover:bg-transparent disabled:hover:text-white/24"
            >
              <ArrowIcon />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={
        desktopSurface
          ? 'flex min-h-0 flex-1 flex-col bg-transparent'
          : 'flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border border-white/10 bg-white/[0.025]'
      }
      // crucible#2610 Phase 3 — the TV (surface="tv") gets a DISTINCT testid
      // from chat/phone. It's the same card component, but under the
      // consumer-preview flag the TV renders it as THE interview display
      // (question, direction, options) while the phone renders the separate
      // voice/text-only input branch above — both can be live at once, so
      // "one card" lookups need to stay surface-scoped rather than colliding
      // on a shared id.
      data-testid={tvSurface ? 'soul-interview-turn-card-tv' : 'soul-interview-turn-card'}
      // crucible#2672 — the TV preview pane is a resizable 16:9 box (see
      // PreviewOverlay.tsx's DisplayPane) that can render far shorter than
      // this card's fixed-px TV typography assumed. `containerType: 'size'`
      // establishes THIS card's own rendered box (post-aspect-ratio-squeeze)
      // as the query-container reference, so descendant `cqh` units below
      // reflect the box's TRUE available height, not an ancestor's larger,
      // pre-squeeze size. Chat/phone never hit this squeeze (they live in
      // the roomy chat column), so this only matters — and only applies —
      // for the tv branch.
      style={tvSurface ? { containerType: 'size' } : undefined}
    >
      <div
        className={[
          'flex min-h-0 flex-1 flex-col',
          // crucible#2672 — tv is `overflow-y-auto`, not `-hidden`: this is
          // the outermost boundary of the "give the options grid a
          // protected minimum, let everything above it compress or scroll"
          // strategy (see DirectionBlock's and ForkQuestion's comments). At
          // realistic preview-pane sizes the clamp()-based sizing above
          // should mean this never actually needs to scroll; it exists so
          // a genuinely pathological box size is "purposeful, signaled
          // overflow" (a scrollbar) rather than a silently vanished option
          // — the failure mode this issue reports. Chat/phone are
          // unaffected (unchanged `overflow-hidden`) — they never hit this
          // squeeze.
          // 2026-07-10 round 2 — bigger outer breathing room on TV (3cqh →
          // 3.5cqh gap, p-3cqh → p-4cqh) so the frame itself reads as
          // considered rather than the content huddling in a corner of a
          // large box. Chat/phone unchanged.
          tvSurface
            ? 'gap-[3.5cqh] overflow-y-auto p-[4cqh]'
            : desktopSurface
              ? 'gap-3 overflow-y-auto px-8 pb-5 pt-4 lg:px-14 [@media(min-height:1050px)]:gap-4 [@media(min-height:1050px)]:pt-6'
              : 'gap-2.5 overflow-hidden p-3 pb-2.5',
        ].join(' ')}
        data-testid={desktopSurface ? 'soul-interview-content' : undefined}
      >
        {/* ── The turn's big heading (agent-authored, e.g. "Shape the first
            impression") — replaces the gold direction card (thesis + wow-beat
            + reference chip). Sean, live-testing, 2026-07-10: "there's no gold
            box with more detail... let's make sure the agent generates all
            the content" — the pitch itself still exists (`turn.direction`)
            and still feeds the model, it just isn't shown on screen anymore.
            No question-count chip either (the interview is LLM-adaptive, not
            a fixed N — a fake "Question 1 of 4" would be dishonest). */}
        {turn.question && desktopSurface ? (
          <div className="shrink-0">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-crucible-accent">
              Question {turn.questionNumber} · {turn.question.axis}
            </p>
            <h1 className="mt-2 max-w-4xl text-[clamp(1.75rem,3vw,2.75rem)] font-light leading-[1.08] tracking-[-0.025em] text-white/95 [@media(min-height:1050px)]:mt-3 [@media(min-height:1050px)]:text-[clamp(2rem,3.2vw,3rem)]">
              {turn.question.prompt || turn.question.title}
            </h1>
            <p className="mt-2 text-sm leading-relaxed text-white/48 [@media(min-height:1050px)]:mt-3">
              {isMoodboardTurn
                ? 'Describe a change, or use this direction when the look feels right.'
                : 'Choose the option that best fits your vision — or write your own.'}
            </p>
          </div>
        ) : (
          turn.question && <QuestionTitle title={turn.question.title} tv={tvSurface} />
        )}

        {/* ── Current question + options ──────────────────────────────────── */}
        {turn.question && (
          <ForkQuestion
            question={turn.question}
            chosen={picked}
            chosenViaRecommendation={pickedViaRecommendation}
            onPick={handlePick}
            onDecideForMe={handleDecideForMe}
            disabled={saving || thinking}
            moodboard={isMoodboardTurn}
            boardVisuals={boardVisuals}
            boardVisualAttempts={boardVisualAttempts}
            onRetryVisual={onRetryVisual}
            steering={steering}
            tv={tvSurface}
            desktop={desktopSurface}
          />
        )}

        {/* ── Free-form input (always available alongside options, except on
            TV — the phone card is where the player types/speaks) ────────── */}
        {turn.question && !tvSurface && (
          <div
            className={
              desktopSurface ? 'flex shrink-0 flex-col gap-2' : 'flex shrink-0 flex-col gap-1'
            }
            data-input-primitive="voice-first-text-fallback"
          >
            <label
              htmlFor="soul-interview-freeform"
              className={
                desktopSurface
                  ? 'text-sm font-medium text-white/72'
                  : 'text-[10.5px] font-medium uppercase tracking-[0.12em] text-white/35'
              }
            >
              {desktopSurface
                ? isMoodboardTurn
                  ? 'Describe what to change'
                  : 'Write your own answer'
                : 'Voice or text'}
            </label>
            <div className="relative flex-1 scroll-mb-4">
              <textarea
                id="soul-interview-freeform"
                data-testid="soul-interview-freeform"
                value={freeText}
                onChange={(e) => {
                  setFreeText(e.target.value);
                  setCommandSource('text');
                }}
                onFocus={(event) => {
                  if (desktopSurface) {
                    event.currentTarget.scrollIntoView({ block: 'center', inline: 'nearest' });
                  }
                }}
                disabled={thinking || saving}
                rows={desktopSurface ? 3 : 1}
                placeholder={
                  desktopSurface
                    ? isMoodboardTurn
                      ? 'For example: make it warmer and add lanterns…'
                      : 'Describe how the game should handle this…'
                    : 'Type a tweak'
                }
                className={
                  desktopSurface
                    ? 'min-h-24 w-full resize-y rounded-xl border border-white/12 bg-white/[0.025] px-4 pb-12 pr-16 pt-3 text-sm leading-relaxed text-white/90 placeholder:text-white/28 focus:border-crucible-accent/60 focus:outline-none disabled:opacity-55'
                    : 'h-11 w-full resize-none overflow-hidden rounded-xl border border-white/10 bg-white/[0.03] py-3 pl-3 pr-14 text-[13px] leading-snug text-white/90 placeholder:text-white/25 focus:border-crucible-accent/50 focus:outline-none'
                }
              />
              <button
                type="button"
                title={speech.supported ? 'Voice answer' : 'Type answer'}
                aria-label="Voice answer"
                aria-pressed={speech.listening}
                data-testid="soul-interview-voice-button"
                onClick={handleVoice}
                disabled={thinking || saving || !speech.supported}
                className={[
                  'absolute flex h-11 w-11 items-center justify-center rounded-full border transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-crucible-accent disabled:cursor-not-allowed disabled:opacity-45',
                  desktopSurface ? 'bottom-2 right-2' : 'bottom-0 right-0',
                  speech.listening
                    ? 'border-crucible-accent bg-crucible-accent text-black shadow-md shadow-crucible-accent/20'
                    : 'border-white/10 bg-[#17151b] text-white/60 hover:border-white/20 hover:bg-white/[0.08]',
                ].join(' ')}
              >
                <MicIcon />
              </button>
            </div>
            {/* #2992 sweep — mic denial/failure must be loud, not a dead button. */}
            {speech.error && (
              <p
                role="alert"
                data-testid="soul-interview-voice-error"
                className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-[12px] text-red-200"
              >
                {speech.error}
              </p>
            )}
          </div>
        )}
      </div>

      {/* ── Thinking row / submit footer ──────────────────────────────────── */}
      {/* crucible#2661 Bugbot round 3 (545ddba4): the TV surface is DISPLAY,
          not input — this footer's Continue button submits onAnswer, which is
          a no-op on the TV (the phone drives answers). A prominent, fully-
          styled Continue pill that does nothing on tap is a consumer-facing
          lie. Suppressed entirely for tv, not just the button: the "Selected:
          X" / "Question N" status text it also carries is redundant on the TV
          (the option grid below already shows the pick via border/background/
          checkmark highlighting on a surface big enough to read directly),
          and the thinking-row branch here never actually fires for the TV
          mount in practice — Workspace.tsx's NorthStarCreateInterviewTv only
          renders this card when `turn?.question` is truthy, and
          IntegratedSoulInterview always clears `current` to null in the same
          state update that sets `thinking` true, so the TV falls through to
          its own separate hand-rolled thinking/finalizing panel instead
          whenever thinking is actually true. */}
      {turn.question && !tvSurface ? (
        <div
          className={
            desktopSurface
              ? 'flex shrink-0 flex-wrap items-center gap-4 border-t border-white/10 px-8 py-5 lg:px-14'
              : 'flex shrink-0 items-center justify-between gap-3 border-t border-white/10 bg-[#100d16]/95 p-2.5'
          }
          data-testid="soul-interview-action-footer"
          aria-live="polite"
        >
          {!desktopSurface &&
            (saving || thinking ? (
              <ThinkingRow elapsedSec={thinkingElapsedSec} saving={saving} />
            ) : (
              <p className="min-w-0 truncate text-[12px] leading-snug text-white/45">
                {phoneSurface && pickedLabel
                  ? `Ready: ${pickedLabel}`
                  : pickedLabel
                    ? `Selected: ${pickedLabel}`
                    : `Question ${turn.questionNumber}`}
              </p>
            ))}
          <div className={desktopSurface ? 'flex flex-wrap items-center gap-4' : ''}>
            {desktopSurface && isMoodboardTurn ? (
              <>
                <button
                  type="button"
                  data-testid="soul-moodboard-steer-send"
                  disabled={
                    saving || thinking || steering || !onSteer || freeText.trim().length === 0
                  }
                  onClick={handleSteerSubmit}
                  className="glint-halo brand-gradient-fill inline-flex min-h-[44px] shrink-0 items-center gap-2 rounded-xl bg-crucible-accent px-5 py-2.5 text-sm font-semibold text-black shadow-lg shadow-crucible-accent/20 transition hover:brightness-110 disabled:cursor-default disabled:opacity-50"
                >
                  {steering ? 'Updating…' : 'Update the look'}
                  {!steering && <ArrowIcon />}
                </button>
                <button
                  type="button"
                  data-testid="soul-moodboard-lock-in"
                  onClick={handleLockIn}
                  disabled={saving || thinking || steering}
                  className="min-h-[44px] rounded-xl border border-white/22 px-5 py-2.5 text-sm font-medium text-white/82 transition hover:border-white/40 hover:text-white disabled:cursor-not-allowed disabled:opacity-45"
                >
                  {turn.question.options[0]?.label ?? 'Use this direction'}
                </button>
              </>
            ) : (
              <button
                type="button"
                data-testid="soul-interview-next"
                disabled={saving || thinking || (picked === null && freeText.trim().length === 0)}
                onClick={handleSubmit}
                className="glint-halo brand-gradient-fill inline-flex min-h-[44px] shrink-0 items-center gap-2 rounded-xl bg-crucible-accent px-5 py-2.5 text-sm font-semibold text-black shadow-lg shadow-crucible-accent/20 transition hover:brightness-110 disabled:cursor-default disabled:opacity-50"
              >
                {saving ? (
                  <>
                    <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-black/30 border-t-black" />
                    Saving…
                  </>
                ) : thinking ? (
                  <>
                    <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-black/30 border-t-black" />
                    Thinking…
                  </>
                ) : (
                  <>
                    Continue
                    <ArrowIcon />
                  </>
                )}
              </button>
            )}
            {desktopSurface && onFinishInterview && (
              <button
                type="button"
                data-testid="integrated-soul-skip"
                onClick={onFinishInterview}
                disabled={finishInterviewDisabled || saving || thinking}
                className="min-h-[44px] rounded-xl border border-white/22 px-5 py-2.5 text-sm font-medium text-white/82 transition hover:border-white/40 hover:text-white disabled:cursor-not-allowed disabled:opacity-45"
              >
                {finishInterviewTarget === 'design'
                  ? 'Skip to Design'
                  : 'Finish interview & review plan'}
              </button>
            )}
          </div>
          {desktopSurface && onFinishInterview && finishInterviewTarget === 'build' && (
            <p className="w-full text-xs leading-relaxed text-white/42">
              You’ll review and approve the plan before Foundry starts building.
            </p>
          )}
        </div>
      ) : null}
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

/**
 * The turn's big heading — agent-authored (e.g. "Shape the first
 * impression"), replacing the old gold direction card (thesis + reference
 * chip + wow-beat). Sean, live-testing, 2026-07-10: the direction card was
 * "too much information to consume" and the reference chip was already
 * dropped (2026-07-09); this drops the rest of the box and puts the turn's
 * own headline where the eye lands first instead. `turn.direction` (the
 * thesis/referenceBar/wowBeat) still exists and still feeds the model — it's
 * a display-only omission, not a removal of the underlying data.
 */
function QuestionTitle({ title, tv = false }: { title: string; tv?: boolean }): React.JSX.Element {
  return (
    <h2
      className={[
        'soul-rise-in shrink-0 font-bold leading-[1.15] tracking-[-0.015em] text-white',
        // 2026-07-10 round 2 — Sean, live-testing: the card "looks very
        // plain... not using the TV space properly" once the dead-space fix
        // (shrink-0 + auto grid rows) stopped the box from stretching. The
        // fix there was correct (no more artificial growth into blank
        // space) but left the ceiling too low for a roomy TV pane — a
        // bigger cqh multiplier + cap lets the type genuinely fill the
        // room it has instead of topping out early and leaving the rest
        // blank. Raised across title/prompt/options/badges together so the
        // whole card scales as one composition, not just the headline.
        tv ? 'text-[clamp(22px,6.4cqh,58px)]' : 'text-[22px]',
      ].join(' ')}
      data-testid="soul-interview-title"
    >
      {title}
    </h2>
  );
}

/** Animated "preparing the next question…" row for the thinking state. Only
 *  ever rendered on chat/phone — the TV surface's action footer (which hosts
 *  this) is suppressed entirely (crucible#2661 Bugbot round 3). */
function ThinkingRow({
  elapsedSec,
  saving = false,
}: {
  elapsedSec?: number;
  saving?: boolean;
}): React.JSX.Element {
  return (
    <div
      className="flex items-center gap-2 text-[12px] text-white/50"
      data-testid="soul-interview-thinking"
    >
      <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/15 border-t-crucible-accent" />
      <span>
        {saving ? 'Saving your answer' : 'Preparing the next question'}
        {!saving && (elapsedSec !== undefined && elapsedSec > 0 ? ` · ${elapsedSec}s` : '…')}
      </span>
    </div>
  );
}

/** One forking taste question: the prompt, the axis label, and 3 selectable
 *  directions. For the moodboard turn each direction also shows its candidate
 *  board image (shimmer while it generates). `tv` scales typography for
 *  10-foot readability (crucible#2610 Phase 3). */
function ForkQuestion({
  question,
  chosen,
  chosenViaRecommendation,
  onPick,
  onDecideForMe,
  disabled = false,
  moodboard = false,
  boardVisuals,
  boardVisualAttempts,
  onRetryVisual,
  steering = false,
  tv = false,
  desktop = false,
}: {
  question: SoulIntakeQuestionView;
  chosen: number | null;
  chosenViaRecommendation: boolean;
  onPick: (optionIndex: number) => void;
  onDecideForMe: () => void;
  disabled?: boolean;
  moodboard?: boolean;
  boardVisuals?: ReadonlyMap<number, TurnVisualMedia | null | undefined>;
  boardVisualAttempts?: ReadonlyMap<number, SoulIntakeTurnVisual['attempt']>;
  onRetryVisual?: () => void;
  steering?: boolean;
  tv?: boolean;
  desktop?: boolean;
}): React.JSX.Element {
  const recommendation = recommendedOptionIndex(question);
  return (
    <div
      className={[
        'flex min-h-0 flex-col',
        // 2026-07-10 — tv drops `flex-1` for the generic option-list case
        // (Sean, live-testing: "has a lot of space" — the removal of the
        // gold direction card + Bold badge left less content, and flex-1 was
        // stretching this section to fill the WHOLE leftover height of a
        // roomy TV pane, showing as dead space below the options). `shrink-0`
        // sizes to content instead; the ancestor's own `overflow-y-auto`
        // (see the card's content-wrapper comment) still catches a
        // genuinely tight box as a scrollbar, so the crucible#2672
        // protection is unchanged — only the "roomy pane" case (empty
        // space, not overflow) changes. Chat/phone keep `flex-1` unchanged
        // — they live in the chat column, not a fixed-height pane.
        //
        // 2026-07-15 — the moodboard turn is the OPPOSITE case: Sean,
        // live-testing: "full moodboard doesn't display (should take up
        // most of the TV)". A single dominant image is exactly the content
        // that SHOULD stretch to fill the pane (unlike 1-3 short option
        // rows, which look wrong stretched) — `flex-1` here, combined with
        // MoodboardSingleBoard's own flex-1 image area, lets the board
        // genuinely fill the remaining height instead of sitting inside a
        // small fixed cqh-capped box with dead space around it.
        desktop
          ? moodboard
            ? 'min-h-0 flex-1 gap-2'
            : 'shrink-0 gap-2'
          : tv
            ? moodboard
              ? 'min-h-0 flex-1 gap-[clamp(0.5rem,2cqh,1.5rem)]'
              : 'shrink-0 gap-[clamp(0.5rem,2cqh,1.5rem)]'
            : 'flex-1 gap-2 overflow-hidden',
      ].join(' ')}
      data-testid="soul-interview-question"
    >
      {question.prompt && !desktop && (
        <div className={tv ? 'shrink' : 'shrink-0'}>
          {/* Secondary line now that QuestionTitle (the big "Shape the X"
              heading) carries the primary weight — the axis caption that used
              to sit here is gone too (redundant with the title). Empty for
              the moodboard turn (2026-07-12, Sean live-testing: "no reason
              for text on this screen... or just a style title") — its
              `title` ("Lock the style anchor") plus the 3 large images
              already say everything this sentence used to restate. */}
          <p
            className={[
              'leading-snug text-white/60',
              tv ? 'text-[clamp(14px,4cqh,34px)]' : 'text-[13px]',
            ].join(' ')}
          >
            {question.prompt}
          </p>
        </div>
      )}
      {/*
       * crucible#2672 — the options list is what the requirement protects:
       * every option must be visible, or overflow must be purposeful and
       * signaled (there is no phone-side option list to fall back on). TV
       * gets `shrink-0` (unlike the heading above, which gives up space
       * first) PLUS a real minimum floor per grid row (`minmax(64px,auto)`
       * — the row never shrinks below 64px, but ALSO never grows past its
       * own content — chosen to fit a 2-line label plus a 2-line flavor at
       * their smallest clamp sizes), so this section is the LAST thing to
       * lose space, not the first. Whatever still doesn't fit is caught by
       * the card's own outer `overflow-y-auto` fallback (see the card's
       * content-wrapper comment) rather than clipped invisibly.
       *
       * 2026-07-10 — `auto` replaces the prior `1fr` (Sean, live-testing:
       * "has a lot of space" — `1fr` grows each row to fill whatever's LEFT
       * in a roomy TV pane, spreading 3 short rows across the whole box
       * instead of packing them at their natural height). `auto` keeps the
       * same 64px floor (still protects the tight-box case) without the
       * grow-to-fill behavior that caused the dead space.
       *
       * 2026-07-13 — the moodboard turn on TV gets its own single large card
       * (`MoodboardSingleBoard` below) instead of this vertical option list.
       * The turn collapsed from 3 candidate boards to 1 (asset-steering
       * replaces pick-one — Sean: "3 moodboards is too many, we should do 1
       * with asset steering"), so the pre-crucible#2740 build-time 3-up grid
       * this used to port no longer fits; one big card matches the same
       * asset-review visual language (`TvSteeringSurface.tsx`'s single-asset
       * card) instead. Non-tv (phone/chat) is untouched — it's the
       * interactive tap-to-pick surface and the moodboard turn's phone side
       * is now its own dedicated steering composer (see the `phoneSurface &&
       * isMoodboardTurn` branch above), not this list. Desktop also uses the
       * single-board presentation, with its steering and acceptance actions
       * rendered in the card footer above.
       */}
      {moodboard && (tv || desktop) ? (
        <MoodboardSingleBoard
          option={question.options[0]}
          visual={boardVisuals?.get(0)}
          attempt={boardVisualAttempts?.get(0)}
          onRetry={onRetryVisual}
          steering={steering}
        />
      ) : (
        <div
          className={[
            'grid min-h-0',
            desktop
              ? 'shrink-0 auto-rows-[minmax(64px,auto)] gap-2'
              : tv
                ? 'shrink-0 auto-rows-[minmax(72px,auto)] gap-[clamp(0.5rem,1.6cqh,1.25rem)]'
                : 'flex-1 auto-rows-fr gap-1.5 overflow-y-auto',
          ].join(' ')}
        >
          {question.options.map((opt, oi) => {
            const selected = chosen === oi;
            // crucible#2661 Bugbot round 3 (545ddba4): the TV surface is
            // DISPLAY, not input — tap-me styling (hover states, button
            // semantics) on a surface nothing can tap is a consumer-facing
            // lie, since onPick is never wired to anything on the TV (the
            // phone drives answers via voice/text; see the Workspace mount).
            //
            // crucible#2672 — round 3 removed the interactive SEMANTICS
            // (role/onClick) but kept the interactive-looking CHROME (a
            // bordered, filled, rounded-rect "card" per option still reads as
            // tappable even with nothing wired to it). TV now gets its own
            // list-row treatment: no border, no fill, no shadow, no rounded
            // "surface" shape — a plain divider between rows. The selected
            // highlight survives as EMPHASIS (accent-colored text + the
            // existing checkmark badge), not as an affordance. 2026-07-10 —
            // the "Bold" recommended-option badge is gone too (Sean, live-
            // testing: "the bold/chosen thing does not need to be seen"); all
            // three options now read as equally weighted until picked.
            // Chat/phone are genuinely interactive and keep the
            // FULL pre-#2672 button chrome below, selected state included —
            // round 1 of this fix accidentally dropped the `selected ? ...`
            // branch here entirely while splitting out the tv-only styling
            // above, silently regressing the picked-option highlight on
            // chat/phone (Bugbot round 2). Restored verbatim; see the
            // per-surface RTL tests below that now pin both directions so
            // this can't silently regress again.
            const optionClassName = [
              'group/opt relative flex h-full min-h-[52px] w-full items-start gap-3 overflow-hidden rounded-xl border text-left',
              desktop ? 'p-4' : 'p-2.5',
              'transition duration-200 ease-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-crucible-accent',
              'disabled:cursor-not-allowed disabled:opacity-45',
              selected
                ? 'soul-option-pop border-crucible-accent/70 bg-crucible-accent/[0.12] shadow-md shadow-crucible-accent/10'
                : [
                    'border-white/10 bg-white/[0.03]',
                    disabled
                      ? ''
                      : 'hover:-translate-y-px hover:border-white/20 hover:bg-white/[0.06]',
                  ].join(' '),
            ].join(' ');
            // 2026-07-10 round 2 — Sean, live-testing: "looks very plain...
            // not using the TV space properly." Swaps the plain bottom-
            // divider row for the SAME left-accent-bar treatment as the
            // hand-rolled "Current pick" panel (Workspace.tsx's
            // NorthStarCreateInterviewTv fallback, ~line 5486-5495) that Sean
            // called out as the more polished reference — a colored left
            // border + generous left padding reads as considered structure
            // without border/fill/shadow/rounded-rect chrome, so it stays
            // display-only (crucible#2661/#2672: nothing here should look
            // tappable on a surface nothing can tap).
            const tvOptionClassName = [
              'relative flex h-full w-full items-start gap-[clamp(0.625rem,1.8cqh,1rem)] overflow-hidden border-l-2 py-[clamp(0.5rem,1.4cqh,1rem)] pl-[clamp(0.75rem,2cqh,1.25rem)]',
              selected ? 'soul-option-pop border-crucible-accent' : 'border-white/15',
            ].join(' ');
            // 2026-07-10 — a lettered badge (A/B/C) replaces the plain
            // checkmark-on-select bullet (Sean, live-testing: "instead of
            // bullets they should be Option A, B, C as that's easier to
            // respond to and reference in text or voice"). Always visible
            // (not "empty until picked" like the old checkmark), selected
            // state still reads as emphasis via the accent fill.
            const optionLetter = String.fromCharCode(65 + oi); // 0→A, 1→B, 2→C
            const checkBadgeClassName = [
              'mt-0.5 flex shrink-0 items-center justify-center rounded-full border font-bold transition',
              tv
                ? 'h-[clamp(1.25rem,3.2cqh,2rem)] w-[clamp(1.25rem,3.2cqh,2rem)] text-[clamp(10px,2.6cqh,18px)]'
                : 'h-[20px] w-[20px] text-[10px]',
              selected
                ? 'border-crucible-accent bg-crucible-accent text-black'
                : 'border-white/25 text-white/55',
            ].join(' ');
            const optionContent = (
              <>
                <span aria-hidden className={checkBadgeClassName}>
                  {optionLetter}
                </span>
                {moodboard && <BoardThumbnail visual={boardVisuals?.get(oi)} index={oi} tv={tv} />}
                <div className="min-w-0 flex-1">
                  <div className="flex items-start gap-2">
                    <span
                      className={[
                        'line-clamp-2 font-medium leading-snug',
                        tv
                          ? 'text-[clamp(15px,4.4cqh,36px)]'
                          : desktop
                            ? 'text-base text-white/92'
                            : 'text-[13.5px] text-white/90',
                        tv ? (selected ? 'text-crucible-accent' : 'text-white/90') : '',
                      ].join(' ')}
                    >
                      {opt.label}
                    </span>
                  </div>
                  {opt.flavor && (
                    <p
                      className={[
                        'mt-0.5 line-clamp-2 leading-snug text-white/45',
                        tv ? 'text-[clamp(11px,3.2cqh,26px)]' : desktop ? 'text-sm' : 'text-[12px]',
                      ].join(' ')}
                    >
                      {opt.flavor}
                    </p>
                  )}
                </div>
              </>
            );
            return tv ? (
              <div
                key={oi}
                data-testid={`soul-interview-option-${oi}`}
                data-selected={selected ? 'true' : 'false'}
                className={tvOptionClassName}
              >
                {optionContent}
              </div>
            ) : (
              <button
                key={oi}
                type="button"
                data-testid={`soul-interview-option-${oi}`}
                data-selected={selected ? 'true' : 'false'}
                aria-pressed={selected}
                disabled={disabled}
                onClick={() => onPick(oi)}
                className={optionClassName}
              >
                {optionContent}
              </button>
            );
          })}
          {!moodboard && !tv && recommendation !== null && (
            <button
              type="button"
              data-testid="soul-interview-decide-for-me"
              data-selected={chosenViaRecommendation ? 'true' : 'false'}
              data-resolved-option-index={recommendation}
              aria-pressed={chosenViaRecommendation}
              aria-label={`Decide for me: ${question.options[recommendation].label}`}
              disabled={disabled}
              onClick={onDecideForMe}
              className={[
                'group/opt relative flex h-full min-h-[52px] w-full items-start gap-3 overflow-hidden rounded-xl border text-left transition duration-200 ease-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-crucible-accent disabled:cursor-not-allowed disabled:opacity-45',
                desktop ? 'p-4' : 'p-2.5',
                chosenViaRecommendation
                  ? 'soul-option-pop border-crucible-accent/70 bg-crucible-accent/[0.12] shadow-md shadow-crucible-accent/10'
                  : disabled
                    ? 'border-white/10 bg-white/[0.03]'
                    : 'border-white/10 bg-white/[0.03] hover:-translate-y-px hover:border-white/20 hover:bg-white/[0.06]',
              ].join(' ')}
            >
              <span
                className={[
                  'mt-0.5 flex h-[20px] w-[20px] shrink-0 items-center justify-center rounded-full border transition',
                  chosenViaRecommendation
                    ? 'border-crucible-accent bg-crucible-accent text-black'
                    : 'border-white/25 text-white/55',
                ].join(' ')}
              >
                <Sparkles aria-hidden="true" size={12} strokeWidth={1.8} />
              </span>
              <div className="min-w-0 flex-1">
                <span className="line-clamp-2 font-medium leading-snug text-white/92">
                  Decide for me
                </span>
                <p className="mt-0.5 line-clamp-2 text-sm leading-snug text-white/45">
                  Foundry will use the recommended answer for this game.
                </p>
              </div>
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * 2026-07-13 — the moodboard turn's TV/desktop presentation: ONE large card (the
 * turn collapsed from 3 pick-one candidates to a single steerable board —
 * Sean: "3 moodboards is too many, we should do 1 with asset steering").
 * Same card language as the build-time asset-review single-asset card
 * (`TvSteeringSurface.tsx`'s `AssetCard`): rounded card, aspect-video image
 * area, a status row underneath — reused deliberately per Sean's steer that
 * this consumer surface "should look unified" with that one.
 *
 * The board itself is purely display — no hover, role, or click handler. TV
 * remains display-only, while desktop renders steering and acceptance actions
 * beside the board. While `steering` is true (a regenerate is in flight), an
 * "Updating…" overlay covers the existing image so neither surface reads as
 * frozen mid-reshape, mirroring the asset-review card's "Tweaking" status.
 */
function MoodboardSingleBoard({
  option,
  visual,
  steering,
  attempt,
  onRetry,
}: {
  option: SoulIntakeQuestionView['options'][number] | undefined;
  visual: TurnVisualMedia | null | undefined;
  steering: boolean;
  attempt?: SoulIntakeTurnVisual['attempt'];
  onRetry?: () => void;
}): React.JSX.Element {
  const ready = visual != null;
  const gaveUp = visual === null;
  // Same honesty rule as `BoardThumbnail`: only the genuinely-in-flight case
  // (present-undefined or not-yet-pushed) gets the animated "Generating…"
  // state — a `null` backstop-timeout gets the static "No preview"
  // placeholder, never an animation that implies generation is still
  // happening when it has already given up.
  const generating = !ready && !gaveUp;
  return (
    <div
      className="group relative flex min-h-0 flex-1 flex-col overflow-hidden rounded-[clamp(0.75rem,2.5cqh,1.75rem)] border border-white/10 bg-black/30 text-left shadow-xl shadow-black/20"
      data-testid="soul-moodboard-card"
      data-ready={ready ? 'true' : 'false'}
      data-steering={steering ? 'true' : 'false'}
    >
      {/* 2026-07-15 — Sean, live-testing (round 1): a scrollbar appeared on
          the TV surface because this box was pure `aspect-video w-full`
          with no height ceiling, so at a wide card width the 16:9-derived
          height exceeded the card's available height, tripping the outer
          content-wrapper's `overflow-y-auto` fallback. A fixed cqh cap
          fixed the overflow but (round 2) left the board noticeably
          SMALLER than the pane, with dead space around it — Sean: "full
          moodboard doesn't display (should take up most of the TV)". Fix:
          this box (and its ForkQuestion/card ancestors, moodboard-only) are
          now `flex-1` instead of a fixed cqh height, so the image genuinely
          fills whatever space the title/status row leave. The image uses
          `object-contain`: modest dark letterboxing preserves the complete
          generated composition instead of cropping away reviewable content. */}
      <div className="relative min-h-0 w-full flex-1 bg-black/45">
        {ready ? (
          <img
            src={visual!.dataUrl}
            alt={visual!.alt}
            data-testid="soul-moodboard-image"
            className="h-full w-full object-contain"
          />
        ) : generating ? (
          <div
            data-testid="soul-moodboard-generating"
            data-generating="true"
            role="status"
            aria-label="Generating the look"
            className="flex h-full w-full flex-col items-center justify-center gap-1.5"
            style={{
              background:
                'linear-gradient(100deg, rgba(255,255,255,0.04) 30%, rgba(255,255,255,0.12) 50%, rgba(255,255,255,0.04) 70%)',
              backgroundSize: '200% 100%',
              animation: 'soul-visual-shimmer 1.6s infinite',
            }}
          >
            <span className="h-[clamp(1.5rem,4.5cqh,2.5rem)] w-[clamp(1.5rem,4.5cqh,2.5rem)] animate-spin rounded-full border-2 border-white/15 border-t-crucible-accent" />
            <span className="text-[clamp(11px,2.6cqh,16px)] font-medium uppercase tracking-[0.08em] text-white/45">
              Generating…
            </span>
          </div>
        ) : (
          <div
            data-testid="soul-moodboard-no-preview"
            className="flex h-full w-full flex-col items-center justify-center gap-3 px-6 text-center"
          >
            <span className="text-[clamp(11px,2.6cqh,16px)] font-medium uppercase tracking-[0.08em] text-white/45">
              Visual preview unavailable
            </span>
            <span className="text-xs normal-case tracking-normal text-white/35">
              {attempt?.failureCode === 'timeout'
                ? 'Generation took too long.'
                : 'Your direction is saved; retry when you’re ready.'}
            </span>
            {onRetry && (
              <button
                type="button"
                onClick={onRetry}
                className="rounded-lg border border-white/20 px-3 py-2 text-xs font-medium normal-case tracking-normal text-white/80 hover:border-white/35"
              >
                Retry visual
              </button>
            )}
          </div>
        )}
        {ready && steering && (
          <div
            data-testid="soul-moodboard-reshaping"
            role="status"
            aria-label="Updating the look"
            className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/55 backdrop-blur-sm"
          >
            <span className="h-[clamp(1.5rem,4.5cqh,2.5rem)] w-[clamp(1.5rem,4.5cqh,2.5rem)] animate-spin rounded-full border-2 border-white/20 border-t-crucible-accent" />
            <span className="text-[clamp(11px,2.6cqh,16px)] font-medium uppercase tracking-[0.08em] text-white/85">
              Updating…
            </span>
          </div>
        )}
      </div>
      <div className="flex items-center justify-between gap-2 px-[clamp(0.5rem,1.6cqh,1.25rem)] py-[clamp(0.5rem,1.4cqh,1rem)]">
        <span className="line-clamp-1 text-[clamp(13px,3.2cqh,20px)] font-semibold text-white/88">
          {option?.label ?? 'Lock this look'}
        </span>
        <span
          data-testid="soul-moodboard-status-pill"
          className="shrink-0 text-[clamp(9px,2.2cqh,12px)] font-semibold uppercase tracking-[0.14em] text-white/45"
        >
          {steering ? 'Tweaking' : ready ? 'Ready' : gaveUp ? 'No preview' : 'Generating'}
        </span>
      </div>
    </div>
  );
}

/**
 * #2058 P2 — one candidate board thumbnail inside a moodboard option. Shows the
 * board image once it lands; a shimmer placeholder (same look as
 * `SoulInterviewVisualCard`) while it generates or when images are off.
 *
 * 2026-07-10 — `null` (IntegratedSoulInterview's backstop gave up after its
 * timeout — see VISUAL_GENERATION_TIMEOUT_MS) gets a plain, STATIC "no
 * preview" placeholder, not the animated shimmer: an animated shimmer implies
 * generation is still in flight, which is exactly the dishonest state Sean
 * flagged ("moodboards didn't generate" — a shimmer that never resolves).
 */
function BoardThumbnail({
  visual,
  index,
  tv,
}: {
  visual: TurnVisualMedia | null | undefined;
  index: number;
  tv: boolean;
}): React.JSX.Element {
  const ready = visual != null;
  const gaveUp = visual === null;
  return (
    <div
      data-testid={`soul-board-thumb-${index}`}
      data-ready={ready ? 'true' : 'false'}
      // 2026-07-12 — Sean, live-testing: "really hard to see the
      // moodboards." The old `h-[44px] w-[64px]` was a FIXED size while
      // every sibling in this card (title, flavor, badge) already scales
      // with the TV pane via `cqh` clamps — the thumbnail was the one
      // thing that didn't grow with the surface. `aspect-video` matches
      // the boards' actual 1280x720 generation size; the tv clamp lets it
      // fill a real fraction of the row instead of staying pinned tiny.
      // Phone/chat (tight, narrow column) keeps the original compact size.
      className={[
        'relative shrink-0 overflow-hidden rounded-md border border-white/10 bg-white/[0.04]',
        tv ? 'aspect-video h-[clamp(3.5rem,18cqh,7.5rem)] mt-0' : 'mt-0.5 h-[44px] w-[64px]',
      ].join(' ')}
    >
      {ready ? (
        <img
          src={visual.dataUrl}
          alt={visual.alt}
          data-testid={`soul-board-image-${index}`}
          className="h-full w-full object-cover"
        />
      ) : gaveUp ? (
        <div
          data-testid={`soul-board-empty-${index}`}
          aria-hidden
          className="flex h-full w-full items-center justify-center bg-white/[0.02] text-[8px] font-medium uppercase tracking-[0.08em] text-white/25"
        >
          No preview
        </div>
      ) : (
        <div
          data-testid={`soul-board-shimmer-${index}`}
          aria-hidden
          className="h-full w-full"
          style={{
            background:
              'linear-gradient(100deg, rgba(255,255,255,0.04) 30%, rgba(255,255,255,0.12) 50%, rgba(255,255,255,0.04) 70%)',
            backgroundSize: '200% 100%',
            animation: 'soul-visual-shimmer 1.6s infinite',
          }}
        />
      )}
    </div>
  );
}

// Icons: ArrowIcon / MicIcon moved to SteerComposer.tsx (crucible#3187) and
// imported above — kept as the single source now that this file no longer
// hand-rolls its own copies.
